import { Small } from "@/components/ui/typography";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import {
  DEFAULT_QUOTE_PRICE_ROUNDING,
  QUOTE_PRICE_ROUNDING_INCREMENT_OPTIONS,
  QuotePriceRounding,
  ROUNDING_MODE_LABELS,
  RoundingMode,
} from "@forgepoint/shared";

interface QuotePriceRoundingRowProps {
  /** Current rounding config, or null when rounding is off (legacy behavior). */
  value: QuotePriceRounding | null;
  /** Called with the next config, or null to turn rounding off. */
  onChange: (value: QuotePriceRounding | null) => void;
  /** Whether the controls are disabled. */
  disabled?: boolean;
}

/**
 * Settings row for configuring how displayed quote prices are rounded.
 * Provides an on/off toggle plus mode, increment, and decimals controls.
 * Turning the toggle off sets the whole config to null (legacy default behavior).
 * @param props - QuotePriceRoundingRowProps
 * @returns Quote price rounding settings row element
 */
export function QuotePriceRoundingRow({
  value,
  onChange,
  disabled = false,
}: QuotePriceRoundingRowProps) {
  const enabled = value !== null;

  const handleToggle = (next: boolean) => {
    onChange(next ? DEFAULT_QUOTE_PRICE_ROUNDING : null);
  };

  const handleModeChange = (mode: RoundingMode) => {
    if (!value) {
      return;
    }
    onChange({ ...value, mode });
  };

  const handleIncrementChange = (increment: string) => {
    if (!value) {
      return;
    }
    onChange({ ...value, increment });
  };

  const incrementOptions =
    value &&
    !QUOTE_PRICE_ROUNDING_INCREMENT_OPTIONS.some(
      (o) => o.value === value.increment,
    )
      ? [
          { value: value.increment, label: `$${value.increment}` },
          ...QUOTE_PRICE_ROUNDING_INCREMENT_OPTIONS,
        ]
      : QUOTE_PRICE_ROUNDING_INCREMENT_OPTIONS;

  const handleDecimalsChange = (raw: string) => {
    if (!value) {
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    const decimals = Number.isNaN(parsed)
      ? 0
      : Math.min(4, Math.max(0, parsed));
    onChange({ ...value, decimals });
  };

  return (
    <SettingsRow
      label="Price Rounding"
      description="Round displayed quote prices to a configurable increment."
    >
      <div className="flex flex-col items-end gap-3">
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={disabled}
        />

        {enabled && value && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Small className="text-muted-foreground">Mode</Small>
              <Select
                value={value.mode}
                onValueChange={(next) =>
                  handleModeChange(RoundingModeFromString(next))
                }
                disabled={disabled}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROUNDING_MODE_LABELS) as RoundingMode[]).map(
                    (mode) => (
                      <SelectItem key={mode} value={mode}>
                        {ROUNDING_MODE_LABELS[mode]}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Small className="text-muted-foreground">Increment</Small>
              <Select
                value={value.increment}
                onValueChange={handleIncrementChange}
                disabled={disabled}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {incrementOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Small className="text-muted-foreground">Decimals</Small>
              <Input
                type="number"
                min={0}
                max={4}
                step={1}
                value={value.decimals}
                onChange={(e) => handleDecimalsChange(e.target.value)}
                disabled={disabled}
                className="w-40"
              />
            </div>
          </div>
        )}
      </div>
    </SettingsRow>
  );
}

/**
 * Narrows an arbitrary select value string to a RoundingMode, defaulting to
 * "NEAREST" when the value is not a recognised mode.
 * @param value - The raw select value
 * @returns The matching RoundingMode
 */
function RoundingModeFromString(value: string): RoundingMode {
  if (value === "UP" || value === "DOWN" || value === "NEAREST") {
    return value;
  }
  return "NEAREST";
}
